import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import {
  createHighlightRegex,
  productGridSkeletonHtml,
  productsInlineMarkup,
  productsPanelMarkup,
  suggestionsMarkup,
} from '@theme/predictive-search-constructor-render';
import {
  buildAutocompleteOptions,
  formatSuggestionCount,
  getPredictiveSearchTranslations,
  normalizeAutocompleteResponse,
  toCacheKey,
} from '@theme/predictive-search-constructor-data';
import {
  DEFAULT_BATCH_PREDICTIVE_SECTION_ID,
  escapeHtml,
  extractProductHandle,
  fetchBatchCards,
  getClonedCardFragment,
  hasBatchCards,
  waitForConstructorClient,
} from '@theme/constructor-utils';

const INPUT_DEBOUNCE_MS = 300;
const HOVER_DEBOUNCE_MS = 150;
const HOVER_INTENT_PREFETCH_MS = 70;
const MOBILE_PRODUCT_LIMIT = 6;
const DESKTOP_PRODUCT_LIMIT = 9;
const MOBILE_PRODUCT_FETCH_LIMIT = 10;
const DESKTOP_PRODUCT_FETCH_LIMIT = 15;
const POPULAR_SEARCH_TERMS_LIMIT = 8;
const HOVER_PREFETCH_LIMIT = 3;

/**
 * @typedef {object} ConstructorAutocompleteProduct
 * @property {string} [id]
 * @property {string} [value]
 * @property {string} [url]
 * @property {{ id?: string, url?: string, product_url?: string, handle?: string }} [data]
 */

/**
 * @typedef {object} ConstructorSuggestion
 * @property {string} text
 * @property {number} resultCount
 * @property {string} url
 */

/**
 * @typedef {object} ConstructorQueryData
 * @property {ConstructorAutocompleteProduct[]} products
 * @property {number} totalResults
 * @property {string} query
 * @property {number} fetchedAt
 */
/**
 * @typedef {HTMLElement & { toggleDialog?: () => void, closeDialog?: () => Promise<void> }} DialogHost
 */

/**
 * @typedef {Window & { ConstructorioSdkLoader?: { injectSdk?: () => void } }} WindowWithConstructorLoader
 */

class PredictiveSearchConstructorComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton', 'productsPanel'];

  #controller = new AbortController();
  /** @type {AbortController | null} */
  #searchAbortController = null;
  /** @type {AbortController | null} */
  #hoverAbortController = null;
  /** @type {Map<string, ConstructorQueryData>} */
  #suggestionCache = new Map();
  #currentQuery = '';
  #primarySuggestionQuery = '';
  #hoveredSuggestion = '';
  #hoverIntentSuggestion = '';
  #isKeyboardNavigation = false;
  #desktopProductsMedia = window.matchMedia('(min-width: 1080px)');
  #isDesktopProducts = this.#desktopProductsMedia.matches;
  /** @type {string[]} */
  #popularSearchTerms = [];
  #popularSearchResultId = '';
  #popularSearchNumResults = 0;
  /** @type {Map<string, Promise<ConstructorQueryData | null>>} */
  #suggestionRequests = new Map();
  /** localStorage key for recent search terms */
  static #RECENT_STORAGE_KEY = 'cnstrc-predictive-search-recent';
  /** Max number of recent search terms to store */
  static #RECENT_MAX = 5;

  /** @returns {HTMLInputElement} */
  get #searchInput() {
    return /** @type {HTMLInputElement} */ (this.refs.searchInput);
  }

  /** @returns {HTMLElement} */
  get #resultsContainer() {
    return /** @type {HTMLElement} */ (this.refs.predictiveSearchResults);
  }

  /** @returns {HTMLElement} */
  get #resetButtonEl() {
    return /** @type {HTMLElement} */ (this.refs.resetButton);
  }

  /** @returns {HTMLElement} */
  get #productsPanel() {
    return /** @type {HTMLElement} */ (this.refs.productsPanel);
  }

  /** @returns {DialogHost | null} */
  get dialog() {
    return /** @type {DialogHost | null} */ (this.closest('dialog-component'));
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const { dialog } = this;

    if (this.#searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
      this.addEventListener('click', this.#handleModalClick, { signal });
      this.addEventListener('click', this.#handleSuggestionClick, { signal });
      this.addEventListener('click', this.#handleResultsClick, { signal });
      this.addEventListener('pointerenter', this.#handleSuggestionPointerEnter, {
        signal,
        capture: true,
      });
      this.addEventListener('pointerleave', this.#handleComponentPointerLeave, {
        signal,
      });
    }

    this.addEventListener('click', this.#handleRecentSearchesClick, { signal });

    if (typeof this.#desktopProductsMedia.addEventListener === 'function') {
      this.#desktopProductsMedia.addEventListener('change', this.#handleProductsMediaChange, {
        signal,
      });
    } else if (typeof this.#desktopProductsMedia.addListener === 'function') {
      this.#desktopProductsMedia.addListener(this.#handleProductsMediaChange);
      signal.addEventListener(
        'abort',
        () => {
          this.#desktopProductsMedia.removeListener(this.#handleProductsMediaChange);
        },
        { once: true },
      );
    }

    this.#resetSearch();

    const nativeDialog = /** @type {HTMLDialogElement | null} */ (this.closest?.('dialog') ?? null);
    if (nativeDialog?.open && !this.#searchInput.value.trim()) {
      requestAnimationFrame(() => {
        this.#renderRecentSearches();
        void this.fetchPopularSearchTerms();
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();

    if (this.#searchAbortController) {
      this.#searchAbortController.abort();
      this.#searchAbortController = null;
    }

    if (this.#hoverAbortController) {
      this.#hoverAbortController.abort();
      this.#hoverAbortController = null;
    }

    this.search.cancel();
    this.#queueHoverSuggestion.cancel();
    this.#queueHoverPrefetch.cancel();
    this.#suggestionRequests.clear();
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.cnstrc-predictive-search__suggestion-item, .cnstrc-predictive-search__products-grid .product-card, .cnstrc-predictive-search__products-grid .predictive-search-results__card',
      ),
    );

    return /** @type {HTMLElement[]} */ (containers.filter((item) => item instanceof HTMLElement));
  }

  get #currentIndex() {
    return (
      this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ??
      -1
    );
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({
          behavior: prefersReducedMotion() ? 'instant' : 'smooth',
          block: 'nearest',
        });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /** @param {KeyboardEvent} event */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;
      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;
      case 'Enter':
        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const query = this.#searchInput.value.trim();
          this.#addToRecentSearches(query);
          if (!query) return;
          window.location.href = this.#buildSearchUrl(query);
        }
        break;
    }
  };

  search = debounce(
    /** @param {InputEvent} event */ (event) => {
      if (!event.inputType) return;

      const searchTerm = this.#searchInput.value.trim();
      this.#currentIndex = -1;

      if (!searchTerm.length) {
        this.#resetSearch();
        return;
      }

      this.#showResetButton();
      this.#performSearch(searchTerm);
    },
    INPUT_DEBOUNCE_MS,
  );

  #queueHoverSuggestion = debounce(
    /** @param {string} query @param {number} resultCount */ (query, resultCount) => {
      this.#loadSuggestionProducts(query, resultCount);
    },
    HOVER_DEBOUNCE_MS,
  );

  #queueHoverPrefetch = debounce(
    /** @param {string} query @param {number} resultCount */ (query, resultCount) => {
      void this.#prefetchSuggestionProducts(query, resultCount, { warmCards: true });
    },
    HOVER_INTENT_PREFETCH_MS,
  );

  /** @param {MediaQueryListEvent} event */
  #handleProductsMediaChange = (event) => {
    const previousIsDesktop = this.#isDesktopProducts;
    this.#isDesktopProducts = event.matches;

    if (previousIsDesktop === this.#isDesktopProducts) return;

    const activeCacheKey = this.#activeProductsCacheKey();
    const cached = this.#suggestionCache.get(activeCacheKey);
    if (!cached) {
      this.#setWithProductsPanel(false);
      this.#removeInlineProducts();
      return;
    }

    this.#renderProductsPanel({
      query: cached.query,
      products: cached.products,
      signal: null,
    });
  };

  #getTranslations() {
    return getPredictiveSearchTranslations(typeof Theme !== 'undefined' ? Theme?.translations : {});
  }

  /** @param {KeyboardEvent} event */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog?.();
    }
  };

  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    const sdkLoader = /** @type {WindowWithConstructorLoader} */ (window).ConstructorioSdkLoader;
    sdkLoader?.injectSdk?.();

    requestAnimationFrame(() => {
      this.#searchInput.focus();
      if (!this.#searchInput.value.trim()) {
        this.#renderRecentSearches();
        void this.fetchPopularSearchTerms();
      }
    });
  };

  /** @param {MouseEvent} event */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement) {
      this.#searchInput.focus();
    }
  };

  /** @param {MouseEvent} event */
  #handleSuggestionClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const suggestionItem = target.closest('.cnstrc-predictive-search__suggestion-item');
    if (!suggestionItem || !this.contains(suggestionItem)) return;

    const query = /** @type {HTMLElement} */ (suggestionItem).dataset.query || '';
    if (!query) return;
    this.#addToRecentSearches(query);
    window.location.href = this.#buildSearchUrl(query);
  };

  /** @param {MouseEvent} event */
  #handleResultsClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = target.closest?.('.quick-add__button');
    if (!button || !this.contains(button)) return;

    const quickAdd = /** @type {{ handleClick?: (e: Event) => void } | null} */ (
      button.closest?.('quick-add-component') ?? null
    );
    if (!quickAdd?.handleClick) return;

    const dialog = this.dialog;
    const nativeDialog = /** @type {HTMLDialogElement | null} */ (this.closest?.('dialog') ?? null);
    const closeDialog = dialog?.closeDialog;
    const isSearchOpen = nativeDialog?.open === true && typeof closeDialog === 'function';

    if (isSearchOpen) {
      event.preventDefault();
      event.stopPropagation();
      closeDialog().then(() => {
        requestAnimationFrame(() => {
          quickAdd.handleClick?.(event);
        });
      });
    } else {
      quickAdd.handleClick(event);
    }
  };

  /** @param {PointerEvent} event */
  #handleSuggestionPointerEnter = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const item = target.closest('.cnstrc-predictive-search__suggestion-item');
    if (!item || !this.contains(item)) return;
    if (item.closest('[data-cnstrc-popular-searches]')) return;

    const suggestionItem = /** @type {HTMLElement} */ (item);
    const query = suggestionItem.dataset.query?.trim() || '';
    if (!query) return;

    const resultCount = Number.parseInt(suggestionItem.dataset.resultCount || '0', 10) || 0;
    const cacheKey = this.#toCacheKey(query);
    this.#hoverIntentSuggestion = cacheKey;

    if (cacheKey === this.#hoveredSuggestion) {
      return;
    }

    this.#setSuggestionActive(cacheKey);
    this.#queueHoverPrefetch(query, resultCount);
    this.#queueHoverSuggestion(query, resultCount);
  };

  /** @param {PointerEvent} event */
  #handleComponentPointerLeave = (event) => {
    if (event.target !== this) return;

    this.#queueHoverSuggestion.cancel();
    this.#queueHoverPrefetch.cancel();
    this.#hoverIntentSuggestion = '';

    if (this.#hoverAbortController) {
      this.#hoverAbortController.abort();
      this.#hoverAbortController = null;
    }

    if (!this.#hoveredSuggestion) return;

    this.#revertToDefaultProducts();
  };

  #revertToDefaultProducts() {
    this.#hoveredSuggestion = '';
    this.#setSuggestionActive('');

    const currentCache = this.#suggestionCache.get(this.#activeProductsCacheKey());
    if (!currentCache) return;

    this.#renderProductsPanel({
      query: currentCache.query,
      products: currentCache.products,
      signal: null,
    });
  }

  async fetchPopularSearchTerms() {
    try {
      const signal = this.#controller.signal;
      const client = await this.#waitForConstructorClient(signal);
      if (!client?.recommendations?.getRecommendations || signal.aborted) {
        this.#popularSearchTerms = [];
        this.#popularSearchResultId = '';
        this.#popularSearchNumResults = 0;
        return [];
      }

      const response = await client.recommendations.getRecommendations('popular-searches', {
        section: 'Search Suggestions',
      });
      if (signal.aborted) return [];

      const recommendationPayload = response ?? {};
      const terms = this.#normalizePopularSearchTerms(response).slice(
        0,
        POPULAR_SEARCH_TERMS_LIMIT,
      );
      this.#popularSearchTerms = terms;
      this.#popularSearchResultId = recommendationPayload?.result_id || '';
      this.#popularSearchNumResults = recommendationPayload?.response?.total_num_results || 0;

      if (!this.#searchInput.value.trim()) {
        this.#renderRecentSearches();
      } else {
        this.#renderPopularSearchesSection();
      }

      return terms;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return [];
      this.#popularSearchTerms = [];
      this.#popularSearchResultId = '';
      this.#popularSearchNumResults = 0;
      if (this.#searchInput.value.trim()) {
        this.#renderPopularSearchesSection();
      }
      return [];
    }
  }

  /** @param {string} query */
  async #performSearch(query) {
    this.#currentQuery = query;
    this.#primarySuggestionQuery = '';
    this.#hoveredSuggestion = '';
    this.#hoverIntentSuggestion = '';
    this.#queueHoverSuggestion.cancel();
    this.#queueHoverPrefetch.cancel();
    this.#setSuggestionActive('');
    this.#showLoadingState();

    const signal = this.#createSearchAbortController().signal;

    if (this.#hoverAbortController) {
      this.#hoverAbortController.abort();
      this.#hoverAbortController = null;
    }

    try {
      const client = await this.#waitForConstructorClient(signal);
      if (!client?.autocomplete?.getAutocompleteResults) {
        this.#showErrorState();
        return;
      }

      const response = await client.autocomplete.getAutocompleteResults(
        query,
        this.#buildAutocompleteOptions(),
      );
      if (signal.aborted || query !== this.#currentQuery) return;

      const parsed = normalizeAutocompleteResponse(
        response,
        query,
        this.#productFetchCap(),
        (searchQuery) => this.#buildSearchUrl(searchQuery),
      );
      const hasPredictiveResults = parsed.products.length > 0 || parsed.totalResults > 0;
      this.#setSuggestions(parsed.suggestions, query, !hasPredictiveResults);

      const cacheKey = this.#toCacheKey(query);
      this.#suggestionCache.set(cacheKey, {
        query,
        products: parsed.products,
        totalResults: parsed.totalResults,
        fetchedAt: Date.now(),
      });

      const primarySuggestionQuery = this.#topSuggestionQuery(parsed.suggestions) || query;
      this.#primarySuggestionQuery = primarySuggestionQuery;
      await this.#renderPrimaryProductsPanel({
        sourceQuery: query,
        primaryQuery: primarySuggestionQuery,
        fallbackProducts: parsed.products,
        fallbackTotalResults: parsed.totalResults,
        client,
        signal,
      });

      this.#prefetchTopSuggestions(parsed.suggestions);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      if (signal.aborted || query !== this.#currentQuery) return;
      this.#showErrorState();
    }
  }

  /**
   * @param {ConstructorSuggestion[]} suggestions
   */
  #topSuggestionQuery(suggestions) {
    if (!Array.isArray(suggestions) || !suggestions.length) return '';
    return suggestions[0]?.text?.trim() || '';
  }

  #defaultProductsQuery() {
    return this.#primarySuggestionQuery || this.#currentQuery;
  }

  #activeProductsCacheKey() {
    if (this.#hoveredSuggestion) return this.#hoveredSuggestion;
    const defaultQuery = this.#defaultProductsQuery();
    return defaultQuery ? this.#toCacheKey(defaultQuery) : '';
  }

  /**
   * @param {{
   *   sourceQuery: string,
   *   primaryQuery: string,
   *   fallbackProducts: ConstructorAutocompleteProduct[],
   *   fallbackTotalResults: number,
   *   client: any,
   *   signal: AbortSignal
   * }} params
   */
  async #renderPrimaryProductsPanel({
    sourceQuery,
    primaryQuery,
    fallbackProducts,
    fallbackTotalResults,
    client,
    signal,
  }) {
    const normalizedPrimaryQuery = primaryQuery.trim();
    if (!normalizedPrimaryQuery) {
      this.#primarySuggestionQuery = sourceQuery;
      this.#renderProductsPanel({
        query: sourceQuery,
        products: fallbackProducts,
        signal,
      });
      return;
    }

    const primaryCacheKey = this.#toCacheKey(normalizedPrimaryQuery);
    const sourceCacheKey = this.#toCacheKey(sourceQuery);
    const cachedPrimary = this.#suggestionCache.get(primaryCacheKey);
    if (cachedPrimary) {
      this.#renderProductsPanel({
        query: cachedPrimary.query,
        products: cachedPrimary.products,
        signal,
      });
      return;
    }

    if (primaryCacheKey === sourceCacheKey) {
      this.#primarySuggestionQuery = normalizedPrimaryQuery;
      this.#renderProductsPanel({
        query: normalizedPrimaryQuery,
        products: fallbackProducts,
        signal,
      });
      return;
    }

    try {
      const response = await client.autocomplete.getAutocompleteResults(
        normalizedPrimaryQuery,
        this.#buildAutocompleteOptions(),
      );
      if (signal.aborted || sourceQuery !== this.#currentQuery) return;

      const parsedPrimary = normalizeAutocompleteResponse(
        response,
        normalizedPrimaryQuery,
        this.#productFetchCap(),
        (searchQuery) => this.#buildSearchUrl(searchQuery),
      );

      this.#suggestionCache.set(primaryCacheKey, {
        query: normalizedPrimaryQuery,
        products: parsedPrimary.products,
        totalResults: parsedPrimary.totalResults || fallbackTotalResults,
        fetchedAt: Date.now(),
      });

      this.#renderProductsPanel({
        query: normalizedPrimaryQuery,
        products: parsedPrimary.products,
        signal,
      });
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      if (signal.aborted || sourceQuery !== this.#currentQuery) return;
      this.#primarySuggestionQuery = sourceQuery;
      this.#renderProductsPanel({
        query: sourceQuery,
        products: fallbackProducts,
        signal,
      });
    }
  }

  /**
   * @param {ConstructorSuggestion[]} suggestions
   */
  #prefetchTopSuggestions(suggestions) {
    if (!Array.isArray(suggestions) || !suggestions.length) return;
    if (!this.#currentQuery) return;

    const fallbackResultCount =
      this.#suggestionCache.get(this.#toCacheKey(this.#currentQuery))?.totalResults || 0;
    /** @type {{ query: string, resultCount: number }[]} */
    const uniqueTopSuggestions = [];
    const seen = new Set();
    for (const suggestion of suggestions) {
      const query = suggestion?.text?.trim() || '';
      if (!query) continue;
      const cacheKey = this.#toCacheKey(query);
      if (!cacheKey || seen.has(cacheKey)) continue;
      seen.add(cacheKey);
      uniqueTopSuggestions.push({
        query,
        resultCount: Number.isFinite(suggestion?.resultCount)
          ? suggestion.resultCount
          : fallbackResultCount,
      });
      if (uniqueTopSuggestions.length >= HOVER_PREFETCH_LIMIT) break;
    }

    if (!uniqueTopSuggestions.length) return;

    const prefetchWork = () => {
      uniqueTopSuggestions.forEach(({ query, resultCount }) => {
        void this.#prefetchSuggestionProducts(query, resultCount);
      });
    };

    if ('requestIdleCallback' in globalThis) {
      globalThis.requestIdleCallback(prefetchWork, { timeout: 400 });
      return;
    }

    setTimeout(prefetchWork, 0);
  }

  /**
   * @param {string} query
   * @param {number} resultCount
   * @param {{ warmCards?: boolean }} [options]
   */
  async #prefetchSuggestionProducts(query, resultCount, { warmCards = false } = {}) {
    const queryData = await this.#fetchSuggestionQueryData({
      query,
      resultCount,
      signal: this.#controller.signal,
    });
    if (warmCards) this.#warmBatchCards(queryData);
  }

  /**
   * Warm the batch-card HTML cache for a query's products so settling on the
   * suggestion renders from cache instead of waiting on a section fetch.
   * Fire-and-forget: fetchBatchCards dedupes in-flight requests and stores
   * results in its shared LRU cache, so the later render call resolves instantly.
   * @param {ConstructorQueryData | null} queryData
   */
  #warmBatchCards(queryData) {
    if (!queryData || this.#controller.signal.aborted) return;
    const handles = queryData.products
      .map((product) => extractProductHandle(product))
      .filter((handle) => typeof handle === 'string' && handle.length > 0);
    if (!handles.length) return;
    void this.#fetchBatchCards(handles, this.#controller.signal).catch(() => {});
  }

  /**
   * @param {{ query: string, resultCount: number, signal: AbortSignal | null }} params
   * @returns {Promise<ConstructorQueryData | null>}
   */
  async #fetchSuggestionQueryData({ query, resultCount, signal }) {
    const trimmed = query.trim();
    if (!trimmed || !this.#currentQuery) return null;

    const cacheKey = this.#toCacheKey(trimmed);
    const cached = this.#suggestionCache.get(cacheKey);
    if (cached) return cached;

    const existingRequest = this.#suggestionRequests.get(cacheKey);
    if (existingRequest) {
      return existingRequest;
    }

    const requestPromise = (async () => {
      try {
        if (signal?.aborted) return null;
        const client = await this.#waitForConstructorClient(signal ?? this.#controller.signal);
        if (!client?.autocomplete?.getAutocompleteResults || signal?.aborted) return null;

        const response = await client.autocomplete.getAutocompleteResults(
          trimmed,
          this.#buildAutocompleteOptions(),
        );
        if (signal?.aborted) return null;

        const parsed = normalizeAutocompleteResponse(
          response,
          trimmed,
          this.#productFetchCap(),
          (searchQuery) => this.#buildSearchUrl(searchQuery),
        );
        const queryData = {
          query: trimmed,
          products: parsed.products,
          totalResults: parsed.totalResults || resultCount,
          fetchedAt: Date.now(),
        };
        this.#suggestionCache.set(cacheKey, queryData);
        return queryData;
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return null;
        return null;
      } finally {
        this.#suggestionRequests.delete(cacheKey);
      }
    })();

    this.#suggestionRequests.set(cacheKey, requestPromise);
    return requestPromise;
  }

  /** @param {string} query @param {number} resultCount */
  async #loadSuggestionProducts(query, resultCount) {
    const trimmed = query.trim();
    if (!trimmed || !this.#currentQuery) return;

    const cacheKey = this.#toCacheKey(trimmed);
    if (cacheKey === this.#hoveredSuggestion) return;
    if (cacheKey !== this.#hoverIntentSuggestion) return;

    const signal = this.#createHoverAbortController().signal;
    const cached = this.#suggestionCache.get(cacheKey);

    this.#hoveredSuggestion = cacheKey;
    this.#setSuggestionActive(cacheKey);

    // Fast path: when the suggestion data and its card HTML are both already
    // warm the render is effectively instant, so render directly and avoid
    // flashing skeletons on a prefetched hover.
    if (cached && this.#hasWarmCards(cached)) {
      this.#renderProductsPanel({
        query: cached.query,
        products: cached.products,
        signal,
      });
      return;
    }

    // Otherwise show skeleton tiles for the new query right away so the grid
    // signals that results are loading instead of leaving the previous query's
    // cards on screen while we fetch.
    this.#renderProductsPanel({
      query: trimmed,
      products: [],
      signal,
      loading: true,
    });

    const queryData =
      cached ??
      (await this.#fetchSuggestionQueryData({ query: trimmed, resultCount, signal: null }));
    if (signal.aborted || cacheKey !== this.#hoverIntentSuggestion) return;
    if (!queryData) {
      // Fetch failed: drop the skeletons and restore the default products.
      this.#revertToDefaultProducts();
      return;
    }

    this.#renderProductsPanel({
      query: queryData.query,
      products: queryData.products,
      signal,
    });
  }

  /**
   * Whether the batch-card HTML for a query's products is already cached, so a
   * render would resolve synchronously without a section fetch.
   * @param {ConstructorQueryData} queryData
   * @returns {boolean}
   */
  #hasWarmCards(queryData) {
    const handles = queryData.products
      .map((product) => extractProductHandle(product))
      .filter((handle) => typeof handle === 'string' && handle.length > 0);
    if (!handles.length) return false;
    return hasBatchCards({
      handles,
      searchRoute: Theme.routes.search_url,
      sectionId: DEFAULT_BATCH_PREDICTIVE_SECTION_ID,
    });
  }

  /**
   * Wait for constructor SDK instance.
   * @param {AbortSignal} signal
   * @returns {Promise<any | null>}
   */
  async #waitForConstructorClient(signal) {
    return waitForConstructorClient({
      signal,
      predicate: (client) => Boolean(client?.autocomplete?.getAutocompleteResults),
    });
  }

  #buildAutocompleteOptions() {
    return buildAutocompleteOptions(this.#productFetchCap());
  }

  #productCap() {
    return this.#isDesktopProducts ? DESKTOP_PRODUCT_LIMIT : MOBILE_PRODUCT_LIMIT;
  }

  #productFetchCap() {
    return this.#isDesktopProducts ? DESKTOP_PRODUCT_FETCH_LIMIT : MOBILE_PRODUCT_FETCH_LIMIT;
  }

  /**
   * Ensure the persistent result regions exist inside the results container.
   * @returns {{ suggestions: HTMLElement, products: HTMLElement, extras: HTMLElement }}
   */
  #ensureRegions() {
    const container = this.#resultsContainer;
    let suggestions = /** @type {HTMLElement | null} */ (
      container.querySelector(':scope > [data-cnstrc-region="suggestions"]')
    );
    let products = /** @type {HTMLElement | null} */ (
      container.querySelector(':scope > [data-cnstrc-region="products"]')
    );
    let extras = /** @type {HTMLElement | null} */ (
      container.querySelector(':scope > [data-cnstrc-region="extras"]')
    );

    if (!suggestions || !products || !extras) {
      // Discard any stray content (e.g. recent-search markup written directly to
      // the container) and rebuild a clean region scaffold.
      container.replaceChildren();
      suggestions = this.#createRegion('suggestions');
      products = this.#createRegion('products');
      extras = this.#createRegion('extras');
      container.append(suggestions, products, extras);
    }

    return { suggestions, products, extras };
  }

  /**
   * @param {'suggestions' | 'products' | 'extras'} name
   * @returns {HTMLElement}
   */
  #createRegion(name) {
    const region = document.createElement('div');
    region.className = 'cnstrc-predictive-search__region';
    region.dataset.cnstrcRegion = name;
    return region;
  }

  /**
   * Render suggestions list area.
   * @param {ConstructorSuggestion[]} suggestions
   * @param {string} query
   * @param {boolean} [showNoResults=false]
   */
  #setSuggestions(suggestions, query, showNoResults = false) {
    const { suggestions: container } = this.#ensureRegions();
    container.removeAttribute('aria-busy');
    const t = this.#getTranslations();

    if (!showNoResults && suggestions.length === 0) {
      container.innerHTML = '';
      this.#renderPopularSearchesSection();
      return;
    }

    const title = escapeHtml(t.topSuggestions || 'Top Suggestions');
    const noResultsTemplate = this.#decodeTranslationHtmlEntities(t.noResults || '');
    const noResultsText = escapeHtml(this.#interpolateQueryTranslation(noResultsTemplate, query));
    const noResultsHint = escapeHtml(this.#decodeTranslationHtmlEntities(t.noResultsHint || ''));
    const highlightRegex = createHighlightRegex(query);

    container.innerHTML = suggestionsMarkup({
      title,
      suggestions: showNoResults ? [] : suggestions,
      noResultsText,
      noResultsHint,
      activeCacheKey: this.#hoveredSuggestion,
      highlightRegex,
      toCacheKey: (value) => this.#toCacheKey(value),
      formatSuggestionCount: (count) => this.#formatSuggestionCount(count),
    });
    this.#renderPopularSearchesSection();
  }

  #renderPopularSearchesSection() {
    const { extras } = this.#ensureRegions();
    // A query is active when this runs, so drop any recent-search block left
    // over from the empty state along with the previous popular block.
    extras.querySelector('.cnstrc-predictive-search__recent')?.remove();
    extras.querySelector('[data-cnstrc-popular-searches]')?.remove();
    if (!this.#popularSearchTerms.length) return;
    extras.insertAdjacentHTML('beforeend', this.#getPopularSearchesHtml(this.#popularSearchTerms));
  }

  /**
   * Render product panel for query.
   * @param {{ query: string, products: ConstructorAutocompleteProduct[], signal: AbortSignal | null, loading?: boolean }} params
   */
  async #renderProductsPanel({ query, products, signal, loading = false }) {
    const panel = this.#productsPanel;
    const t = this.#getTranslations();
    const title = t.relatedProducts || 'Products';
    const viewAllText = t.viewAll || 'View all';
    const searchUrl = this.#buildSearchUrl(query);
    const shouldShowProductsPanel = loading || products.length > 0;
    const isDesktopProducts = this.#isDesktopProducts;
    const skeletonHtml = productGridSkeletonHtml(this.#productCap());

    this.#setWithProductsPanel(isDesktopProducts && shouldShowProductsPanel);
    if (!shouldShowProductsPanel) {
      this.#removeInlineProducts();
      return;
    }

    if (isDesktopProducts) {
      this.#removeInlineProducts();
      const existingDesktopProducts = panel.querySelector('[data-cnstrc-products]');
      if (existingDesktopProducts instanceof HTMLElement) {
        this.#updateProductsShell({
          productsContainer: existingDesktopProducts,
          title,
          query,
          searchUrl,
          viewAllText,
        });
      } else {
        panel.innerHTML = productsPanelMarkup({
          title,
          query,
          searchUrl,
          viewAllText,
          skeletonHtml,
        });
      }
    } else {
      this.#setInlineProducts({
        title,
        query,
        searchUrl,
        viewAllText,
        skeletonHtml,
      });
    }

    const injectEl = this.#cardsInjectElement(isDesktopProducts);
    if (!injectEl) return;

    if (loading) {
      // Always show skeleton tiles while loading so the section keeps a stable
      // height (instead of collapsing) and never shows stale cards from the
      // previous query while the user is still typing.
      injectEl.innerHTML = skeletonHtml;
      injectEl.setAttribute('aria-busy', 'true');
      return;
    }

    injectEl.setAttribute('aria-busy', 'true');
    const handles = products
      .map((product) => extractProductHandle(product))
      .filter((handle) => typeof handle === 'string' && handle.length > 0);

    if (!handles.length) {
      injectEl.replaceChildren();
      injectEl.removeAttribute('aria-busy');
      return;
    }

    try {
      const cardMap = await this.#fetchBatchCards(handles, signal);
      if (signal?.aborted) return;
      this.#replaceCardsContent({
        injectEl,
        cardMap,
        handles: handles.slice(0, this.#productCap()),
      });
      injectEl.removeAttribute('aria-busy');
      injectEl.dispatchEvent(
        new CustomEvent('cnstrc:cards-initialized', {
          bubbles: true,
          detail: { container: injectEl, query },
        }),
      );
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      injectEl.replaceChildren();
      injectEl.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {{ productsContainer: HTMLElement, title: string, query: string, searchUrl: string, viewAllText: string }} params
   */
  #updateProductsShell({ productsContainer, title, query, searchUrl, viewAllText }) {
    productsContainer.dataset.cnstrcProductsQuery = query;

    const titleEl = productsContainer.querySelector('[data-cnstrc-products-title]');
    if (titleEl) {
      titleEl.textContent = `${title} "${query}"`;
    }

    const viewAllLink = productsContainer.querySelector('[data-cnstrc-products-view-all]');
    if (viewAllLink instanceof HTMLAnchorElement) {
      viewAllLink.href = searchUrl;
      viewAllLink.textContent = viewAllText;
    }
  }

  /**
   * @param {boolean} isDesktopProducts
   * @returns {HTMLElement | null}
   */
  #cardsInjectElement(isDesktopProducts) {
    const injectEl = isDesktopProducts
      ? this.#productsPanel.querySelector('[data-cnstrc-cards-inject]')
      : this.#resultsContainer.querySelector(
          '[data-cnstrc-products-inline] [data-cnstrc-cards-inject]',
        );
    return injectEl instanceof HTMLElement ? injectEl : null;
  }

  /**
   * @param {{ injectEl: HTMLElement, cardMap: Map<string, DocumentFragment>, handles: string[] }} params
   */
  #replaceCardsContent({ injectEl, cardMap, handles }) {
    const fragment = document.createDocumentFragment();
    /** @type {HTMLElement[]} */
    const enteringCards = [];
    handles.forEach((handle) => {
      const cardFragment = getClonedCardFragment(cardMap, handle);
      if (cardFragment) {
        const cardElement = cardFragment.querySelector('product-card');
        if (cardElement instanceof HTMLElement) {
          cardElement.classList.add('cnstrc-predictive-search__card-enter');
          enteringCards.push(cardElement);
        }
        fragment.appendChild(cardFragment);
      }
    });
    injectEl.replaceChildren(fragment);

    if (!enteringCards.length || prefersReducedMotion()) {
      return;
    }

    requestAnimationFrame(() => {
      enteringCards.forEach((card) => {
        if (card.isConnected) {
          card.classList.add('is-visible');
        }
      });
    });
  }

  #removeInlineProducts() {
    this.#resultsContainer.querySelector('[data-cnstrc-products-inline]')?.remove();
  }

  /**
   * @param {{
   *   title: string,
   *   query: string,
   *   searchUrl: string,
   *   viewAllText: string,
   *   skeletonHtml: string
   * }} params
   */
  #setInlineProducts({ title, query, searchUrl, viewAllText, skeletonHtml }) {
    const { products } = this.#ensureRegions();
    const existingInlineProducts = products.querySelector('[data-cnstrc-products-inline]');
    if (existingInlineProducts instanceof HTMLElement) {
      // Update the existing shell in place so the section stays mounted and the
      // grid keeps its skeleton/cards rather than being torn down and rebuilt.
      this.#updateProductsShell({
        productsContainer: existingInlineProducts,
        title,
        query,
        searchUrl,
        viewAllText,
      });
      return;
    }

    const markup = productsInlineMarkup({ title, query, searchUrl, viewAllText, skeletonHtml });
    products.insertAdjacentHTML('beforeend', markup);
  }

  /**
   * @param {string[]} handles
   * @param {AbortSignal | null} signal
   * @returns {Promise<Map<string, DocumentFragment>>}
   */
  async #fetchBatchCards(handles, signal) {
    const uniqueHandles = [...new Set(handles)];
    if (!uniqueHandles.length) return new Map();

    return fetchBatchCards({
      handles: uniqueHandles,
      signal,
      searchRoute: Theme.routes.search_url,
      sectionId: DEFAULT_BATCH_PREDICTIVE_SECTION_ID,
    });
  }

  #showLoadingState() {
    const { suggestions } = this.#ensureRegions();
    // Keep any existing suggestions/no-results visible while the next request is
    // in flight so the list doesn't collapse to a spinner (which would shift the
    // products section below it). Only show the spinner on the first search.
    const hasExistingResults = suggestions.querySelector(
      '[data-cnstrc-suggestions], [data-cnstrc-no-results]',
    );
    if (hasExistingResults) {
      suggestions.setAttribute('aria-busy', 'true');
    } else {
      suggestions.innerHTML = `
        <div class="cnstrc-predictive-search__loading">
          <div class="cnstrc-predictive-search__loading-spinner"></div>
        </div>
      `;
    }
    this.#renderPopularSearchesSection();

    this.#renderProductsPanel({
      query: this.#defaultProductsQuery() || this.#currentQuery,
      products: [],
      signal: null,
      loading: true,
    });
  }

  #showErrorState() {
    const { suggestions } = this.#ensureRegions();
    suggestions.removeAttribute('aria-busy');
    this.#setWithProductsPanel(false);
    this.#removeInlineProducts();
    const msg = escapeHtml(this.#getTranslations().errorMessage || '');
    suggestions.innerHTML = `
      <div class="cnstrc-predictive-search__error">
        <p>${msg}</p>
      </div>
    `;
    this.#renderPopularSearchesSection();
  }

  #hideResetButton() {
    this.#resetButtonEl.hidden = true;
  }

  #showResetButton() {
    this.#resetButtonEl.hidden = false;
  }

  resetSearch = debounce(() => {
    this.#resetSearch();
  }, 100);

  #resetSearch() {
    this.#currentIndex = -1;
    this.#currentQuery = '';
    this.#primarySuggestionQuery = '';
    this.#hoveredSuggestion = '';
    this.#hoverIntentSuggestion = '';
    this.#suggestionCache.clear();
    this.#suggestionRequests.clear();

    if (this.#searchAbortController) {
      this.#searchAbortController.abort();
      this.#searchAbortController = null;
    }

    if (this.#hoverAbortController) {
      this.#hoverAbortController.abort();
      this.#hoverAbortController = null;
    }

    this.#queueHoverSuggestion.cancel();
    this.#queueHoverPrefetch.cancel();
    this.#searchInput.value = '';
    this.#hideResetButton();
    this.#setWithProductsPanel(false);
    this.#resultsContainer.innerHTML = '';
    this.#renderRecentSearches();
  }

  /** Get recent search terms from localStorage (max 5, most recent first). @returns {string[]} */
  #getRecentSearches() {
    try {
      const raw = localStorage.getItem(PredictiveSearchConstructorComponent.#RECENT_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      const seen = new Set();
      return parsed
        .filter((item) => typeof item === 'string')
        .map((item) => item.trim())
        .filter((item) => {
          if (!item || seen.has(item)) return false;
          seen.add(item);
          return true;
        })
        .slice(0, PredictiveSearchConstructorComponent.#RECENT_MAX);
    } catch {
      return [];
    }
  }

  /**
   * Persist recent searches list.
   * @param {string[]} terms
   */
  #setRecentSearches(terms) {
    try {
      localStorage.setItem(
        PredictiveSearchConstructorComponent.#RECENT_STORAGE_KEY,
        JSON.stringify(terms),
      );
    } catch {
      // localStorage may be full or unavailable
    }
  }

  /**
   * Add a search term to recent searches (on Enter submit). Dedupes and moves to top; keeps max 5.
   * @param {string} term
   */
  #addToRecentSearches(term) {
    const trimmed = term.trim();
    if (!trimmed) return;
    const list = [trimmed, ...this.#getRecentSearches().filter((item) => item !== trimmed)].slice(
      0,
      PredictiveSearchConstructorComponent.#RECENT_MAX,
    );
    this.#setRecentSearches(list);
  }

  /**
   * Remove a recent search by index.
   * @param {number} index
   */
  #removeRecentSearchAt(index) {
    if (!Number.isInteger(index) || index < 0) return;
    const next = this.#getRecentSearches().filter((_, itemIndex) => itemIndex !== index);
    this.#setRecentSearches(next);
  }

  #clearRecentSearches() {
    this.#setRecentSearches([]);
  }

  /**
   * @returns {HTMLElement}
   */
  #recentSearchesContainer() {
    return this.#ensureRegions().extras;
  }

  /**
   * Build HTML for the Recent Searches block (title, list with term link + clear button each, Clear all).
   * @param {string[]} terms
   * @returns {string}
   */
  #getRecentSearchesHtml(terms) {
    if (!terms?.length) return '';
    const t = this.#getTranslations();
    const title = t.recentSearches ?? 'Recent Searches';
    const clearAllText = t.clearAll ?? 'Clear all';
    return `
        <div class="cnstrc-predictive-search__recent" aria-label="${escapeHtml(title)}">
          <div class="cnstrc-predictive-search__recent-header">
            <h3 class="cnstrc-predictive-search__section-title h2">${escapeHtml(title)}</h3>
            <button
              type="button"
              class="cnstrc-predictive-search__recent-clear-all text-link"
              data-cnstrc-recent-action="clear-all"
              aria-label="${escapeHtml(t.clearAllRecentAria || clearAllText)}"
            >
              <span class="animated-underline">${escapeHtml(clearAllText)}</span>
            </button>
          </div>
          <ul class="cnstrc-predictive-search__recent-list" role="listbox">
            ${terms
              .map((term, index) => {
                const safeQuery = escapeHtml(term);
                const safeUrl = this.#buildSearchUrl(term);
                const removeLabel = (t.removeFromRecent || '')
                  .replace(/\{\{\s*term\s*\}\}/g, term)
                  .trim();
                return `
              <li
                class="cnstrc-predictive-search__recent-item"
                role="option"
                data-query="${safeQuery}"
                data-index="${index}"
              >
                <a href="${safeUrl}" class="cnstrc-predictive-search__recent-link">${safeQuery}</a>
                <button
                  type="button"
                  class="cnstrc-predictive-search__recent-clear"
                  data-cnstrc-recent-action="remove"
                  data-cnstrc-index="${index}"
                  aria-label="${escapeHtml(removeLabel || `Remove ${term} from recent searches`)}"
                >
                  <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="25" height="25" rx="12.5" fill="#F0F0F0"/>
                  <rect width="25" height="25" rx="12.5" fill="white" fill-opacity="0.85"/>
                  <path d="M8 8L17.5 17.5" stroke="black"/>
                  <path d="M17.5 8L8 17.5" stroke="black"/>
                  </svg>
                </button>
              </li>
            `;
              })
              .join('')}
          </ul>
        </div>
      `;
  }

  /** Render recent searches block (from localStorage). Hide container if empty. */
  #renderRecentSearches() {
    const container = this.#recentSearchesContainer();
    const recentSearchTerms = this.#getRecentSearches();
    const sections = [];

    if (recentSearchTerms.length) {
      sections.push(this.#getRecentSearchesHtml(recentSearchTerms));
    }

    if (this.#popularSearchTerms.length) {
      sections.push(this.#getPopularSearchesHtml(this.#popularSearchTerms));
    }

    container.innerHTML = sections.join('');
  }

  /**
   * @param {any} response
   */
  #normalizePopularSearchTerms(response) {
    const data = response?.response ?? response ?? {};
    const rawTerms = Array.isArray(data?.results)
      ? data.results
      : Array.isArray(data?.recommendations)
        ? data.recommendations
        : Array.isArray(data?.data?.results)
          ? data.data.results
          : [];

    const seen = new Set();
    /** @type {string[]} */
    const normalizedTerms = [];
    for (const item of rawTerms) {
      const term = this.#extractPopularSearchTerm(item);
      if (!term) continue;
      const key = this.#toCacheKey(term);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      normalizedTerms.push(term);
    }

    return normalizedTerms;
  }

  /**
   * @param {any} item
   */
  #extractPopularSearchTerm(item) {
    if (typeof item === 'string') return item.trim();
    if (!item || typeof item !== 'object') return '';

    const value =
      item?.value ??
      item?.text ??
      item?.query ??
      item?.term ??
      item?.data?.value ??
      item?.data?.text ??
      item?.data?.query ??
      item?.data?.searchTerm ??
      item?.data?.search_term ??
      item?.data?.name ??
      '';

    return typeof value === 'string' ? value.trim() : '';
  }

  /**
   * @param {string[]} terms
   */
  #getPopularSearchesHtml(terms) {
    if (!terms.length) return '';

    const t = this.#getTranslations();
    const title = t.popularSearches || t.topSuggestions || 'Popular Searches';

    return `
      <div class="cnstrc-predictive-search__trending-container
            cnstrc-predictive-search__popular-container"
            data-cnstrc-recommendations
            data-cnstrc-recommendations-pod-id="popular-searches"
            data-cnstrc-popular-searches
            data-cnstrc-result-id="${this.#popularSearchResultId}"
            data-cnstrc-num-results="${this.#popularSearchNumResults}"
          >
        <div class="cnstrc-predictive-search__suggestions cnstrc-predictive-search__trending">
          <h3 class="cnstrc-predictive-search__section-title h2">${escapeHtml(title)}</h3>
          <ul class="cnstrc-predictive-search__suggestions-list" role="listbox">
            ${terms
              .map((term, index) => {
                const safeTerm = escapeHtml(term);
                const cacheKey = this.#toCacheKey(term);
                return `
              <li
                class="cnstrc-predictive-search__suggestion-item"
                role="option"
                data-query="${safeTerm}"
                data-cache-key="${cacheKey}"
                data-cnstrc-popular-item="true"
                data-result-count="0"
                data-index="${index}"
                aria-selected="false"
                data-cnstrc-item="recommendation"
                data-cnstrc-item-id="${safeTerm}"
                data-cnstrc-item-name="${safeTerm}"
                data-cnstrc-item-section="Search Suggestions"
                data-cnstrc-strategy-id="bestsellers"
              >
                <a
                  href="${this.#buildSearchUrl(term)}"
                  class="cnstrc-predictive-search__suggestion-link"
                >
                  ${safeTerm}
                </a>
              </li>
            `;
              })
              .join('')}
          </ul>
        </div>
      </div>
    `;
  }

  /**
   * Handle remove/clear actions via event delegation to avoid rebinding after every render.
   * @param {MouseEvent} event
   */
  #handleRecentSearchesClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const actionButton = target.closest?.('[data-cnstrc-recent-action]');
    if (!(actionButton instanceof HTMLElement) || !this.contains(actionButton)) return;

    const action = actionButton.dataset.cnstrcRecentAction;
    if (!action) return;

    event.preventDefault();
    event.stopPropagation();

    if (action === 'clear-all') {
      this.#clearRecentSearches();
      this.#renderRecentSearches();
      return;
    }

    if (action === 'remove') {
      const index = Number.parseInt(actionButton.dataset.cnstrcIndex || '-1', 10);
      this.#removeRecentSearchAt(index);
      this.#renderRecentSearches();
    }
  };

  /**
   * @param {boolean} withProducts
   */
  #setWithProductsPanel(withProducts) {
    const dialog = this.closest('.search-modal__content') || this.closest('dialog');
    const panel = this.#productsPanel;

    if (withProducts) {
      dialog?.classList.add('search-modal__content--with-products');
      panel.removeAttribute('hidden');
      panel.setAttribute('data-visible', '');
      return;
    }

    dialog?.classList.remove('search-modal__content--with-products');
    panel.removeAttribute('data-visible');
    panel.setAttribute('hidden', '');
    panel.innerHTML = '';
    // Note: the mobile inline products are managed separately (see
    // #setInlineProducts/#removeInlineProducts) so they persist across
    // re-renders. Callers remove them explicitly when there are no products.
  }

  /**
   * @param {string} query
   */
  #buildSearchUrl(query) {
    const searchUrl = new URL(Theme.routes.search_url, window.location.origin);
    searchUrl.searchParams.set('q', query);
    return searchUrl.toString();
  }

  /**
   * @param {string} template
   * @param {string} query
   */
  #interpolateQueryTranslation(template, query) {
    return template.replace(/\{\{\s*(terms|query)\s*\}\}/g, query);
  }

  /**
   * @param {string} value
   */
  #decodeTranslationHtmlEntities(value) {
    const parser = document.createElement('textarea');
    parser.innerHTML = value;
    return parser.value;
  }

  /**
   * @param {number} count
   */
  #formatSuggestionCount(count) {
    const template = this.#getTranslations().resultsCountLabel || '{{ count }} results';
    return escapeHtml(formatSuggestionCount(template, count));
  }

  /**
   * @param {string} value
   */
  #toCacheKey(value) {
    return toCacheKey(value);
  }

  /**
   * @param {string} activeCacheKey
   */
  #setSuggestionActive(activeCacheKey) {
    this.querySelectorAll('.cnstrc-predictive-search__suggestion-item').forEach((item) => {
      const suggestionItem = /** @type {HTMLElement} */ (item);
      const isActive = suggestionItem.dataset.cacheKey === activeCacheKey;
      suggestionItem.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  #createSearchAbortController() {
    if (this.#searchAbortController) {
      this.#searchAbortController.abort();
    }
    this.#searchAbortController = new AbortController();
    return this.#searchAbortController;
  }

  #createHoverAbortController() {
    if (this.#hoverAbortController) {
      this.#hoverAbortController.abort();
    }
    this.#hoverAbortController = new AbortController();
    return this.#hoverAbortController;
  }
}

if (!customElements.get('predictive-search-constructor-component')) {
  customElements.define(
    'predictive-search-constructor-component',
    PredictiveSearchConstructorComponent,
  );
}

export { PredictiveSearchConstructorComponent };
